import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  Phone,
  Mail,
  MessageCircle,
  Megaphone,
  Check,
  Save,
  Info,
  AlertCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import { formatWaNumber } from '../../lib/contactUtils.ts';

export default function TopbarManager() {
  const { settings, updateSettingsOnServer, showToast } = useApp();

  const [form, setForm] = useState({
    topBarEnabled: settings?.topBarEnabled ?? true,
    topBarText: settings?.topBarText || 'Accelerating B2B Sustainability & Green Career Ecosystems',
    topBarCtaText: settings?.topBarCtaText || 'WA Fast Response',
    topBarWaMessage: settings?.topBarWaMessage || 'Halo Admin, saya tertarik dengan layanan Anda.',
    whatsappNumber: settings?.contact?.whatsappNumber || '',
    phone: settings?.contact?.phone || '',
    email: settings?.contact?.email || '',
  });

  const [isSaving, setIsSaving] = useState(false);

  // Sync state if settings change
  useEffect(() => {
    if (settings) {
      setForm({
        topBarEnabled: settings.topBarEnabled ?? true,
        topBarText: settings.topBarText || 'Accelerating B2B Sustainability & Green Career Ecosystems',
        topBarCtaText: settings.topBarCtaText || 'WA Fast Response',
        topBarWaMessage: settings.topBarWaMessage || 'Halo Admin, saya tertarik dengan layanan Anda.',
        whatsappNumber: settings.contact?.whatsappNumber || '',
        phone: settings.contact?.phone || '',
        email: settings.contact?.email || '',
      });
    }
  }, [settings]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const cleanedWa = formatWaNumber(form.whatsappNumber);
      const cleanedPhone = form.phone.trim();
      const cleanedEmail = form.email.trim();

      const updatedContact = {
        whatsappNumber: cleanedWa,
        phone: cleanedPhone,
        email: cleanedEmail,
        address: settings?.contact?.address || '',
        instagram: settings?.contact?.instagram || '',
        youtube: settings?.contact?.youtube || '',
        facebook: settings?.contact?.facebook || '',
        tiktok: settings?.contact?.tiktok || '',
        linkedin: settings?.contact?.linkedin || '',
        twitter: settings?.contact?.twitter || '',
      };

      const ok = await updateSettingsOnServer({
        topBarEnabled: form.topBarEnabled,
        topBarText: form.topBarText.trim(),
        topBarCtaText: form.topBarCtaText.trim(),
        topBarWaMessage: form.topBarWaMessage.trim(),
        contact: updatedContact,
      });

      if (ok) {
        showToast(
          'Pengaturan Area Top Bar berhasil disimpan dan disinkronkan!',
          'success',
          'Berhasil Disimpan'
        );
      }
    } catch (err) {
      console.error(err);
      showToast('Gagal menyimpan pengaturan Top Bar.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div id="topbar-manager-container" className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 p-6 sm:p-8 rounded-3xl text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300 text-xs font-bold border border-indigo-400/30">
            <Megaphone className="w-3.5 h-3.5" />
            <span>Pengaturan Tampilan Teratas</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            Pengaturan Area Top Bar (Bilah Atas)
          </h2>
          <p className="text-sm text-slate-300 max-w-2xl leading-relaxed">
            Kelola teks promo baris teratas, informasi email, nomor hotline telepon, label tombol WhatsApp Fast Response, serta isi template pesan otomatis saat tombol tsb diklik.
          </p>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Form Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Column 1 & 2: Configuration Inputs */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Status & Teks Utama */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-indigo-600" />
                  <span>Status & Teks Baris Atas</span>
                </h3>
                
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, topBarEnabled: !prev.topBarEnabled }))}
                  className={`px-4 py-1.5 rounded-xl text-xs font-extrabold transition-all cursor-pointer ${
                    form.topBarEnabled 
                      ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-xs' 
                      : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                  }`}
                >
                  {form.topBarEnabled ? '✓ Top Bar Aktif' : '✕ Top Bar Dinonaktifkan'}
                </button>
              </div>

              {form.topBarEnabled && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-slate-800 block mb-1">
                      Kata-kata Pengumuman / Promo (Top Bar Text):
                    </label>
                    <textarea
                      rows={2}
                      value={form.topBarText}
                      onChange={(e) => setForm(prev => ({ ...prev, topBarText: e.target.value }))}
                      placeholder="Masukkan kata-kata promosi atau pengumuman yang ingin ditampilkan di bagian paling atas..."
                      className="w-full px-3 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all outline-none font-medium text-slate-800"
                      required
                    />
                    <p className="text-[11px] text-slate-400 mt-1">
                      *Teks ini akan berjalan dinamis atau terpotong rapi secara otomatis di layar mobile jika terlalu panjang.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Hubungi Kontak Resmi */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-4">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2 pb-3 border-b border-slate-100">
                <Phone className="w-5 h-5 text-indigo-600" />
                <span>Informasi Kontak Resmi di Top Bar</span>
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">
                    Nomor WhatsApp Utama (62...):
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400 text-xs font-bold">+</span>
                    <input
                      type="text"
                      value={form.whatsappNumber}
                      onChange={(e) => setForm(prev => ({ ...prev, whatsappNumber: e.target.value }))}
                      placeholder="628123456789"
                      className="w-full pl-6 pr-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-mono text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none"
                    />
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">Gunakan format angka saja (contoh: 6281234567890).</p>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">
                    Nomor Telepon Hotline:
                  </label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+62 812-3456-7890"
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-mono text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Bisa ditulisi teks atau tanda baca bebas.</p>
                </div>

                <div className="md:col-span-2">
                  <label className="text-xs font-bold text-slate-800 block mb-1">
                    Alamat Email Resmi:
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="halo@nusantara-creative.example.com"
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Tombol WA Fast Response & CTA */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-4">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2 pb-3 border-b border-slate-100">
                <MessageCircle className="w-5 h-5 text-indigo-600" />
                <span>Kustomisasi Tombol Fast Response WA</span>
              </h3>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">
                    Tulisan/Label pada Tombol WA Fast Response:
                  </label>
                  <input
                    type="text"
                    value={form.topBarCtaText}
                    onChange={(e) => setForm(prev => ({ ...prev, topBarCtaText: e.target.value }))}
                    placeholder="WA Fast Response"
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">
                    Kata-kata CTA yang Muncul Saat Tombol Diklik (Template Pesan WA):
                  </label>
                  <textarea
                    rows={3}
                    value={form.topBarWaMessage}
                    onChange={(e) => setForm(prev => ({ ...prev, topBarWaMessage: e.target.value }))}
                    placeholder="Contoh: Halo Admin, saya melihat promo di top bar dan ingin bertanya tentang layanan Anda..."
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none"
                    required
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    *Pesan di atas akan secara otomatis terisi di aplikasi WhatsApp konsumen ketika mereka mengeklik tombol tersebut di Top Bar.
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* Column 3: Live Preview & Action */}
          <div className="space-y-6">
            
            {/* Live Preview Card */}
            <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800 shadow-xl space-y-4 text-white">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-800">
                <Eye className="w-4 h-4 text-amber-400" />
                <span className="text-[11px] uppercase font-bold text-amber-400 tracking-wider">
                  Live Preview (Tampilan Atas)
                </span>
              </div>

              {form.topBarEnabled ? (
                <div className="space-y-4">
                  <p className="text-[10px] text-slate-400 leading-relaxed">
                    Berikut adalah visualisasi bagaimana Top Bar akan terlihat oleh pengunjung website Anda di bagian paling atas layar:
                  </p>

                  <div className="bg-slate-950 p-3 rounded-2xl border border-slate-800 space-y-3">
                    {/* Simulated Announcement Text */}
                    <div className="flex items-center gap-1.5 text-[10px] font-medium text-slate-300">
                      <Megaphone className="w-3 h-3 text-amber-400 shrink-0" />
                      <span className="truncate max-w-[200px] block">
                        {form.topBarText || 'Belum ada kata-kata promo.'}
                      </span>
                    </div>

                    <div className="h-[1px] bg-slate-800" />

                    {/* Simulated Contacts & Button */}
                    <div className="flex flex-col gap-2">
                      {form.phone && (
                        <div className="flex items-center gap-1.5 text-[10px] text-slate-300">
                          <Phone className="w-2.5 h-2.5 text-emerald-400" />
                          <span className="font-mono">{form.phone}</span>
                        </div>
                      )}
                      {form.email && (
                        <div className="flex items-center gap-1.5 text-[10px] text-slate-300">
                          <Mail className="w-2.5 h-2.5 text-emerald-400" />
                          <span className="truncate max-w-[180px] block">{form.email}</span>
                        </div>
                      )}
                      {form.whatsappNumber && (
                        <div className="pt-1">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-600 text-white font-bold text-[10px]">
                            <MessageCircle className="w-2.5 h-2.5" />
                            <span>{form.topBarCtaText}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center space-y-2">
                  <EyeOff className="w-8 h-8 mx-auto text-slate-600" />
                  <p className="text-xs text-slate-400">
                    Bilah Atas (Top Bar) sedang dinonaktifkan.
                  </p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-3">
              <button
                type="submit"
                disabled={isSaving}
                className="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-400 text-white font-bold rounded-2xl flex items-center justify-center gap-2 shadow-md transition-all active:scale-98 cursor-pointer text-xs sm:text-sm"
              >
                {isSaving ? (
                  <span>Menyimpan...</span>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Simpan Pengaturan Top Bar</span>
                  </>
                )}
              </button>

              <p className="text-[10px] text-slate-500 text-center leading-relaxed">
                Perubahan akan langsung aktif di seluruh halaman website secara real-time setelah disimpan.
              </p>
            </div>

          </div>

        </div>
      </form>
    </div>
  );
}
